import { useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  Progress,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from '@chakra-ui/react';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { formatCount } from '../lib/bulk-password';

/**
 * Isi tiap langkah modal ganti password massal. Tanpa state bisnis: semua
 * nilai datang dari `bulk-password-modal.jsx`.
 */

const SKIPPED_PREVIEW_LIMIT = 10;
const tabularNums = { fontVariantNumeric: 'tabular-nums' };

export function PasswordFormStep({
  entityLabel,
  defaultLabel,
  mode,
  onModeChange,
  password,
  onPasswordChange,
  error,
  inputRef,
  onSubmit,
}) {
  return (
    <RadioGroup value={mode} onChange={onModeChange}>
      <Stack spacing={4}>
        <Box>
          <Radio value="default">
            Reset ke password default ({defaultLabel})
          </Radio>
          <Text fontSize="sm" color="gray.600" ml={6}>
            Tiap {entityLabel} memakai {defaultLabel}-nya sendiri.
          </Text>
        </Box>
        <Box>
          <Radio value="custom">Ketik password baru</Radio>
          {mode === 'custom' && (
            <CustomPasswordField
              password={password}
              onPasswordChange={onPasswordChange}
              error={error}
              inputRef={inputRef}
              onSubmit={onSubmit}
            />
          )}
        </Box>
      </Stack>
    </RadioGroup>
  );
}

function CustomPasswordField({
  password,
  onPasswordChange,
  error,
  inputRef,
  onSubmit,
}) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onSubmit();
    }
  };

  return (
    <FormControl isInvalid={Boolean(error)} mt={3} pl={6}>
      <FormLabel fontSize="sm" mb={1}>
        Password baru
      </FormLabel>
      <InputGroup>
        <Input
          ref={inputRef}
          type={isVisible ? 'text' : 'password'}
          autoComplete="new-password"
          value={password}
          onChange={(event) => onPasswordChange(event.target.value)}
          onKeyDown={handleKeyDown}
          pr={12}
        />
        <InputRightElement>
          <IconButton
            size="sm"
            variant="ghost"
            aria-label={
              isVisible ? 'Sembunyikan password' : 'Tampilkan password'
            }
            icon={isVisible ? <MdVisibilityOff /> : <MdVisibility />}
            onClick={() => setIsVisible((visible) => !visible)}
          />
        </InputRightElement>
      </InputGroup>
      <FormHelperText>
        Semua akun terpilih akan memakai password ini.
      </FormHelperText>
      <FormErrorMessage>{error}</FormErrorMessage>
    </FormControl>
  );
}

export function ConfirmStep({
  mode,
  defaultLabel,
  changeCount,
  nonDefaultCount,
  missingCount,
}) {
  return (
    <Stack spacing={4}>
      <Text sx={tabularNums}>
        <Text as="strong" fontWeight={600}>
          {formatCount(changeCount)} akun
        </Text>{' '}
        akan diganti passwordnya.
      </Text>
      {nonDefaultCount > 0 && (
        <Alert status="warning" borderRadius="md" alignItems="flex-start">
          <AlertIcon />
          <AlertDescription sx={tabularNums}>
            {formatCount(nonDefaultCount)} akun sudah tidak memakai password
            default ({defaultLabel}). Password mereka saat ini akan ditimpa.
          </AlertDescription>
        </Alert>
      )}
      {mode === 'default' && missingCount > 0 && (
        <Text fontSize="sm" color="gray.700" sx={tabularNums}>
          {formatCount(missingCount)} akun tidak punya {defaultLabel} dan akan
          dilewati.
        </Text>
      )}
    </Stack>
  );
}

export function RunningStep({ processed, total }) {
  const percent = total > 0 ? Math.round((processed / total) * 100) : 0;
  return (
    <Stack spacing={3} py={2}>
      <Progress
        value={percent}
        size="sm"
        colorScheme="telegram"
        hasStripe
        borderRadius="full"
        aria-label="Progres ganti password"
      />
      <Text aria-live="polite" sx={tabularNums}>
        Memproses {formatCount(processed)} dari {formatCount(total)}…
      </Text>
    </Stack>
  );
}

export function ResultStep({ result, skippedItems, onRetry }) {
  const failedCount = result.failedIds.length;
  return (
    <Stack spacing={4}>
      <Text role="status" fontSize="lg" fontWeight={600} sx={tabularNums}>
        {formatCount(result.updated)} berhasil ·{' '}
        {formatCount(skippedItems.length)} dilewati · {formatCount(failedCount)}{' '}
        gagal
      </Text>
      {failedCount > 0 && (
        <Alert status="error" borderRadius="md" alignItems="flex-start">
          <AlertIcon />
          <Box>
            <AlertDescription display="block" sx={tabularNums}>
              {formatCount(failedCount)} akun gagal diproses karena gangguan
              jaringan atau server. Aman untuk dicoba lagi.
            </AlertDescription>
            <Button
              mt={2}
              size="sm"
              variant="outline"
              colorScheme="red"
              bg="white"
              onClick={onRetry}
            >
              Coba lagi yang gagal
            </Button>
          </Box>
        </Alert>
      )}
      {skippedItems.length > 0 && <SkippedList items={skippedItems} />}
    </Stack>
  );
}

function SkippedList({ items }) {
  const preview = items.slice(0, SKIPPED_PREVIEW_LIMIT);
  const restCount = items.length - preview.length;
  return (
    <Box>
      <Text fontSize="sm" fontWeight={600} mb={2}>
        Dilewati
      </Text>
      <List
        maxH="240px"
        overflowY="auto"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="md"
      >
        {preview.map((item) => (
          <ListItem
            key={item.id}
            px={3}
            py={2}
            borderBottomWidth="1px"
            borderColor="gray.100"
            _last={{ borderBottomWidth: 0 }}
          >
            <Flex justify="space-between" columnGap={3} flexWrap="wrap">
              <Text fontSize="sm" fontWeight={500}>
                {item.name}
              </Text>
              <Text fontSize="sm" color="gray.600">
                {item.reason}
              </Text>
            </Flex>
          </ListItem>
        ))}
      </List>
      {restCount > 0 && (
        <Text fontSize="sm" color="gray.600" mt={2} sx={tabularNums}>
          dan {formatCount(restCount)} lainnya
        </Text>
      )}
    </Box>
  );
}
